import { useState } from "react";
import { DateTime } from "luxon";

import type { DateRange, GraphSettings, Resolution } from "../shared/types";

export type { DateRange, GraphSettings, Resolution };

/** DateRange guaranteed to have non-null start/end (always set by useGraphControlState). */
export type NonNullableDateRange = {
  startDate: DateTime;
  endDate: DateTime;
};

type UseGraphControlStateOptions = {
  defaultStartDate?: DateTime;
  defaultEndDate?: DateTime;
  defaultResolution?: Resolution;
};

/**
 * Local control state for graph panels. Each instance is independent
 */
export function useGraphControlState({
  defaultStartDate = DateTime.now().minus({ weeks: 1 }),
  defaultEndDate = DateTime.now(),
  defaultResolution = "hourly",
}: UseGraphControlStateOptions = {}) {
  const [dateRange, setDateRange] = useState<NonNullableDateRange>({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  });
  const [resolution, setResolution] = useState<Resolution>(defaultResolution);
  const [graphSettings, setGraphSettings] = useState<GraphSettings>({
    average: false,
    min: false,
    max: false,
    grid: false,
  });

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange({
      startDate: range.startDate ?? defaultStartDate,
      endDate: range.endDate ?? defaultEndDate,
    });
  };

  return {
    dateRange,
    setDateRange: handleDateRangeChange,
    resolution,
    setResolution,
    graphSettings,
    setGraphSettings,
  };
}
